import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AiQuillEditor(props) {
  const initialContent = `
    <h1>Dress For Baisakhi Festival</h1>
    <h2>Introduction to Baisakhi Festival</h2>
    <p>Baisakhi, a vibrant festival celebrated primarily in Punjab, marks the harvest season and the beginning of the new year for many. This joyous occasion is steeped in cultural significance, filled with lively music, dance, and delicious food. As families gather to celebrate this auspicious day, one aspect that adds to the festive spirit is dressing up in traditional attire.Choosing the right outfit can enhance your experience and connect you deeper with this beautiful celebration. Whether you're heading to a family gathering or joining community festivities, understanding what to wear will help you embrace Baisakhi's rich heritage while showcasing your personal style. Let’s explore how to dress elegantly for this special occasion!</p>
    <h2>Traditional Clothing for Men and Women</h2>
    <p>The Baisakhi Festival brings vibrant traditions to the forefront, especially in clothing. Men often don a kurta paired with dhoti or pajama. The kurta can be simple or richly embroidered, reflecting their personal style.Women typically embrace the elegance of salwar kameez or lehenga choli. Bright colors and intricate patterns are common, symbolizing joy and prosperity. Dupattas add an extra flair to these outfits.Men may also choose a turban as part of their attire, showcasing regional styles that hold cultural significance. Women might adorn themselves with traditional jewelry like jhumkas and bangles for added charm.Comfort is key during celebrations filled with dance and feasting. Opting for breathable fabrics ensures you enjoy every moment without any discomfort while looking fabulous at the same time.</p>
    <h2>Contemporary Twist on Traditional Outfits</h2>
    <p>Incorporating modern elements into traditional outfits can create a stunning fusion. For women, pairing a <a href="https://www.google.com">classic Punjabi</a> suit with tailored jeans adds a fresh vibe. This unique combination is both comfortable and stylish.Men can explore the charm of kurta-pyjama sets with contemporary footwear like loafers or sneakers. This twist not only elevates the look but also makes it more adaptable for different settings.Additionally, vibrant prints and bold colors are making their way into traditional attire. Think floral motifs on lehengas or graphic patterns on sherwanis to make heads turn at any celebration.Layering pieces like denim jackets over kurtas gives an effortless edge while maintaining cultural roots. Accessories too have evolved; statement jewelry combined with subtle designs can enhance the outfit beautifully without overwhelming it.</p>
    <h2>Accessories to Complete the Look</h2>
    <p>Accessories play a vital role in elevating your Baisakhi outfit. They can add a touch of personality and flair to traditional attire. For men, vibrant turbans are essential. Choose one that complements your kurta or sherwani. A well-embroidered dupatta can also enhance the overall look.Women can opt for statement jewelry pieces like jhumkas or chunky bangles that shimmer under the festive lights. A colorful maangtikka adds elegance and draws attention to the face.Don’t forget about footwear! Traditional juttis or loafers provide both comfort and style while dancing during celebrations.Bags should also be considered—an embroidered clutch or potli bag not only serves a purpose but enhances your ensemble as well. Remember, accessories should reflect your personal style while embracing the spirit of Baisakhi.</p>
    <h2>Hairstyles and Makeup Ideas for Baisakhi</h2>
    <p>Baisakhi is a vibrant festival that calls for equally lively hairstyles and makeup. Embrace the spirit of celebration with traditional braids adorned with fresh flowers. A simple yet elegant side braid can add charm while keeping you comfortable.For those preferring an updo, consider a bun embellished with colorful pins or ribbons to reflect the festive hues. Loose waves are also a great choice, offering a soft and romantic look.When it comes to makeup, opt for bright colors that resonate with the joyous atmosphere. Bold lips in shades like crimson or fuchsia can be stunning against richly colored outfits. Don’t shy away from shimmery eye shadows—golds and bronzes work beautifully for adding some sparkle without overwhelming your look. A dash of highlighter on your cheekbones will give you that radiant glow suitable for this special occasion.</p>
    <h2>Tips for Choosing the Perfect Outfit</h2>
    <p>Choosing the perfect outfit for Baisakhi is all about blending tradition with personal style. Start by considering the weather and location of your celebration. Lightweight fabrics like cotton or linen are excellent choices for warm days, ensuring comfort while you enjoy the festivities.Next, think about color. Vibrant hues like yellow, orange, and green reflect the spirit of harvest and joy associated with Baisakhi. Don't hesitate to mix patterns or prints that resonate with your personality but try to maintain a balance to avoid overwhelming your look.Fit is crucial as well. Ensure that whatever you wear allows freedom of movement—after all, you'll want to dance! Tailored outfits can enhance your silhouette while keeping you comfortable.Pay attention to details such as embroidery or embellishments that showcase craftsmanship without being too flashy. When in doubt, simplicity often speaks volumes.By following these tips and infusing them with a touch of creativity, you're sure to shine this Baisakhi festival!</p>
  `;

  const [content, setContent] = useState(initialContent);
  const [wordCount, setWordCount] = useState(0);
  const editorRef = useRef(null);

  const handleChange = (content) => {
    setContent(content);

    const textOnly = content.replace(/<[^>]+>/g, '');
    const words = textOnly.trim().split(/\s+/).filter(Boolean);
    const lines = textOnly.split(/\n/).filter(line => line.trim().length > 0);
    setWordCount(words.length + lines.length);
  };

  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      const container = editor.root;
      container.style.height = 'auto';
      container.style.overflow = 'visible';
    }
  }, []);

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      [{ list: 'ordered'}, { list: 'bullet' }],
      [{ indent: '-1'}, { indent: '+1' }],
  
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['link', 'image',],
      ['clean'],
      [{ color: [] }],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <>
      <div className='quill-editor block w-full rounded-md border-0 py-2 px-4 text-gray-900 placeholder:text-gray-400 outline-none sm:text-sm/6'>
        <ReactQuill
          ref={editorRef}
          value={content}
          onChange={handleChange}
          modules={modules}
          theme="snow"
          placeholder={props.placeholder}
          bounds={'.quill-editor'}
        />
        <p className='text-slate-400 text-xs text-right p-2'>Word Count: {wordCount}</p>
      </div>
      <style jsx>{`
        .ql-toolbar.ql-snow,
        .ql-container.ql-snow {
          border: none !important;
        }

        .ql-container {
          height: auto !important;
          overflow: visible !important;
        }

        .ql-editor {
          min-height: 50px;
          padding-bottom: 20px;
          padding-top: 40px;
        }
          .ql-snow .ql-editor h1{
            line-height: 1.2;
          }
        .ql-snow .ql-editor h1, .ql-snow .ql-editor h2, .ql-snow .ql-editor h3, .ql-snow .ql-editor h4, .ql-snow .ql-editor h5, .ql-snow .ql-editor h6 {
          margin:0 0 15px;
          font-weight: 600;
        }
          .ql-snow .ql-editor p{
            margin-bottom: 15px;
            font-size: 15px;
            line-height: 1.6;
          }
      `}</style>
    </>
  );
}

export default AiQuillEditor;
